/*
#*===================================================================
#*
#* Licensed Materials - Property of GameTimeFootage Inc.
#*
#* Copyright NextPro 2018. All Rights Reserved.
#*
#*===================================================================
*/

import { ModuleWithProviders, NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { CommonModule } from '@angular/common';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { ShareModule } from '@ngx-share/core';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { CookieService } from 'ngx-cookie-service';
import { ClickOutsideModule } from 'ng-click-outside';
import { TranslateModule } from '@ngx-translate/core';
import { ImageCropperModule } from 'ngx-image-cropper';
import { NgSelectModule } from '@ng-select/ng-select';

import { VideoPlayerModule } from '@video-player/video-player.module';

import { ProcessingComponent } from 'app/shared/components/processing/processing.component';
import { AthleteCardComponent } from 'app/shared/components/athlete-card/athlete-card.component';
import { ChannelCardComponent } from 'app/shared/components/channel-card/channel-card.component';
import { VideoCardV2Component } from 'app/shared/components/video-card-v2/video-card-v2.component';
import { InnerMenuSmallComponent } from 'app/shared/components/inner-menu-small/inner-menu-small.component';
import { DropdownComponent } from 'app/shared/components/dropdown/dropdown.component';
import { SvgIconComponent } from 'app/shared/components/svg-icon/svg-icon.component';
import { RelatedVideosComponent } from 'app/shared/components/video-context/related-videos/related-videos.component';
import { RostersComponent } from 'app/shared/components/video-context/rosters/rosters.component';
import { BreakdownComponent } from 'app/shared/components/video-context/breakdown-tray/breakdown/breakdown.component';
import { BreakdownTrayComponent } from 'app/shared/components/video-context/breakdown-tray/breakdown-tray.component';
import { NotFoundComponent } from 'app/shared/containers/not-found/not-found.component';
import { StyleguideComponent } from 'app/shared/containers/styleguide/styleguide.component';
import { FullPageModalComponent } from 'app/shared/containers/full-page-modal/full-page-modal.component';
import { VideoLibraryComponent } from 'app/shared/containers/video-library/video-library.component';
import { EllipsisMenuComponent } from 'app/shared/components/ellipsis-menu/ellipsis-menu.component';

import * as fromServices from 'app/shared/services';
import * as fromGuards from 'app/shared/guards';
import { effects, reducers } from 'app/shared/store';
import { FromDatePipe } from 'app/shared/pipes/from-date.pipe';
import { NumberShortener } from 'app/shared/pipes/number-shortener.pipe';
import { CtaEmptyContentPageComponent } from 'app/shared/components/cta-empty-content-page/cta-empty-content-page.component';
import { EmojiPipe } from 'app/shared/pipes/emoji.pipe';
import { ScrollToDirective } from 'app/shared/directives/scroll-to.directive';
import { PhoneNumberFormatDirective } from 'app/shared/directives/phone-number-format.directive';
import { DateFormatDirective } from 'app/shared/directives/date-format.directive';
import { GpaFormatDirective } from 'app/shared/directives/gpa-format.directive';
import { TimeFormatDirective } from 'app/shared/directives/time-format.directive';
import { ModalScrollDirective } from 'app/shared/directives/modal-scroll.directive';
import { LazyLoadingDirective } from 'app/shared/directives/lazy-loading.directive';

import { ShareButtonComponent } from 'app/shared/components/share-button/share-button.component';
import { CustomTypeAheadComponent } from 'app/shared/components/custom-type-ahead/custom-type-ahead.component';
import { DynamicTranslationsComponent } from 'app/shared/components/dynamic-translations/dynamic-translations.component';
import { SystemNotificationComponent } from 'app/shared/components/system-notification/system-notification.component';
import { LogoutComponent } from 'app/shared/containers/logout/logout.component';
import { EventV2Component } from 'app/shared/components/event-v2/event-v2.component';
import { CarouselComponent } from 'app/shared/components/carousel/carousel.component';
import { ToggleSwitchComponent } from 'app/shared/components/toggle-switch/toggle-switch.component';
import { FeaturedVideoItemComponent } from './components/featured-video-item/featured-video-item.component';
import { ConfirmationModalComponent } from './components/confirmation-modal/confirmation-modal.component';
import { ModalComponent } from './components/modal/modal.component';
import { StickyComponent } from './components/sticky/sticky.component';
import { LoadingBarComponent } from './components/loading-bar/loading-bar.component';
import { LoginFormComponent } from './components/login-form/login-form.component';
import { ImgFallbackDirective } from './directives/img-fallback.directive';
import { HrGameSelectFormComponent } from './components/hr-game-select-form/hr-game-select-form.component';
import { EventDetailsService } from 'app/events/services/event-details.service';

import { modalComponents } from 'app/shared/modals';
import { VideoCarouselComponent } from './components/video-carousel/video-carousel.component';
import { EmptySectionComponent } from 'app/profile/components/empty-section/empty-section.component';
import { LibraryService } from '@library/services';
import { ProfileService } from '@profile/services';
import { InTrayPurchaseComponent } from './components/video-context/breakdown-tray/in-tray-purchase/in-tray-purchase.component';
import { RosterUploadComponent } from './components/video-context/breakdown-tray/roster-upload/roster-upload.component';

@NgModule({
  exports: [
    AthleteCardComponent,
    ChannelCardComponent,
    EventV2Component,
    InnerMenuSmallComponent,
    VideoCardV2Component,
    FromDatePipe,
    NumberShortener,
    ProcessingComponent,
    SvgIconComponent,
    DropdownComponent,
    NotFoundComponent,
    StyleguideComponent,
    CtaEmptyContentPageComponent,
    ShareButtonComponent,
    FullPageModalComponent,
    CustomTypeAheadComponent,
    ReactiveFormsModule,
    InfiniteScrollModule,
    TranslateModule,
    SystemNotificationComponent,
    CarouselComponent,
    ScrollToDirective,
    PhoneNumberFormatDirective,
    DateFormatDirective,
    GpaFormatDirective,
    TimeFormatDirective,
    ModalScrollDirective,
    FeaturedVideoItemComponent,
    ConfirmationModalComponent,
    ModalComponent,
    StickyComponent,
    LoadingBarComponent,
    LoginFormComponent,
    ImgFallbackDirective,
    LazyLoadingDirective,
    VideoCarouselComponent,
    EmptySectionComponent,
    NgSelectModule,
    VideoLibraryComponent,
    RelatedVideosComponent,
    RostersComponent,
    RosterUploadComponent,
    BreakdownComponent,
    BreakdownTrayComponent,
    EllipsisMenuComponent,

    ...modalComponents,
  ],
  imports: [
    CommonModule,
    EffectsModule.forFeature(effects),
    RouterModule,
    StoreModule.forFeature('shared', reducers),
    ClickOutsideModule,
    ReactiveFormsModule,
    InfiniteScrollModule,
    ImageCropperModule,
    FormsModule,
    TranslateModule,
    ShareModule,
    LazyLoadImageModule,
    NgSelectModule,
    ScrollingModule,
    VideoPlayerModule,
  ],
  providers: [...fromServices.services, CookieService, LibraryService, ProfileService],
  declarations: [
    AthleteCardComponent,
    ChannelCardComponent,
    VideoCardV2Component,
    FeaturedVideoItemComponent,
    InnerMenuSmallComponent,
    EmojiPipe,
    ProcessingComponent,
    FromDatePipe,
    NumberShortener,
    SvgIconComponent,
    NotFoundComponent,
    StyleguideComponent,
    DropdownComponent,
    CtaEmptyContentPageComponent,
    ShareButtonComponent,
    FullPageModalComponent,
    CustomTypeAheadComponent,
    DynamicTranslationsComponent,
    SystemNotificationComponent,
    LogoutComponent,
    EventV2Component,
    CarouselComponent,
    ScrollToDirective,
    PhoneNumberFormatDirective,
    DateFormatDirective,
    GpaFormatDirective,
    TimeFormatDirective,
    ModalScrollDirective,
    LazyLoadingDirective,
    ToggleSwitchComponent,
    ConfirmationModalComponent,
    ModalComponent,
    StickyComponent,
    LoadingBarComponent,
    LoginFormComponent,
    ImgFallbackDirective,
    HrGameSelectFormComponent,
    EmptySectionComponent,
    VideoCarouselComponent,
    VideoLibraryComponent,
    RelatedVideosComponent,
    RostersComponent,
    BreakdownComponent,
    BreakdownTrayComponent,
    RosterUploadComponent,
    EllipsisMenuComponent,

    ...modalComponents,

    InTrayPurchaseComponent,
  ],
})
export class SharedModule {
  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [...fromGuards.guards, EventDetailsService],
    };
  }
}
